import { useEffect, useRef, useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { endpoints } from "@/lib/api";
import { NATURE_PHOTOS, shuffle } from "@/lib/naturePhotos";

const IDLE_MS = 30 * 60 * 1000; // 30 minutes
const SLIDE_MS = 5 * 60 * 1000; // 5 minutes per image
const FADE_MS = 1500; // cross-fade duration

export default function Slideshow() {
  const [active, setActive] = useState(false);
  const [now, setNow] = useState(new Date());
  const [photos] = useState(() => shuffle(NATURE_PHOTOS));
  // Two layers cross-fade between each other. `front` is the layer currently fully visible.
  const [layerA, setLayerA] = useState(0); // photo index on layer A
  const [layerB, setLayerB] = useState(1); // photo index on layer B
  const [front, setFront] = useState("A"); // which layer is currently on top (opacity 1)

  const idleTimerRef = useRef(null);
  const slideTimerRef = useRef(null);
  const clockTimerRef = useRef(null);
  const activeRef = useRef(false);
  const nextPhotoRef = useRef(2); // next photo index to load into the back layer

  const { data: todos = [] } = useQuery({
    queryKey: ["todos"],
    queryFn: endpoints.todos,
    enabled: active,
    refetchInterval: active ? 1000 * 60 : false,
  });
  const { data: family = [] } = useQuery({
    queryKey: ["family"],
    queryFn: endpoints.family,
    enabled: active,
  });

  // Inspirational quotes — fetch a batch and refresh every 10 minutes while active.
  const { data: quotesData } = useQuery({
    queryKey: ["quotes"],
    queryFn: endpoints.quotes,
    enabled: active,
    refetchInterval: active ? 10 * 60 * 1000 : false,
    staleTime: 10 * 60 * 1000,
  });
  const quotes = quotesData?.items || [];
  const [quoteIdx, setQuoteIdx] = useState(0);
  const quotePointerRef = useRef(0);
  const currentQuote = quotes.length > 0 ? quotes[quoteIdx % quotes.length] : null;

  const topTodos = useMemo(() => {
    const todayIso = new Date().toISOString().slice(0, 10);
    const rank = (t) => {
      if (t.completed) return 9;
      if (!t.due_date) return 3;
      if (t.due_date < todayIso) return 0;
      if (t.due_date === todayIso) return 1;
      return 2;
    };
    return [...todos]
      .filter((t) => !t.completed)
      .sort((a, b) => {
        const ra = rank(a);
        const rb = rank(b);
        if (ra !== rb) return ra - rb;
        if (a.due_date && b.due_date) return a.due_date.localeCompare(b.due_date);
        if (a.due_date) return -1;
        if (b.due_date) return 1;
        return 0;
      })
      .slice(0, 3);
  }, [todos]);

  const familyById = useMemo(
    () => Object.fromEntries(family.map((f) => [f.id, f])),
    [family]
  );

  // Idle detection runs ONCE. Uses activeRef so we don't tear down listeners on toggle.
  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  useEffect(() => {
    function resetIdle() {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => setActive(true), IDLE_MS);
    }
    function onActivity() {
      if (activeRef.current) {
        setActive(false);
      }
      resetIdle();
    }
    const events = ["mousemove", "touchstart", "keydown", "click", "pointerdown"];
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    resetIdle();
    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity));
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
    };
  }, []);

  // Slideshow advancement: self-rescheduling setTimeout chain (avoids setInterval drift/coalescing).
  useEffect(() => {
    if (!active) return;

    let cancelled = false;

    function scheduleNext() {
      if (cancelled) return;
      slideTimerRef.current = setTimeout(() => {
        if (cancelled) return;
        // Decide which layer is currently in the back and load the next photo into it,
        // then flip `front` to cross-fade.
        setFront((prev) => {
          const nextPhoto = nextPhotoRef.current % photos.length;
          nextPhotoRef.current = (nextPhotoRef.current + 1) % photos.length;
          if (prev === "A") {
            // B is back -> load next photo into B, then bring B to front
            setLayerB(nextPhoto);
            return "B";
          } else {
            setLayerA(nextPhoto);
            return "A";
          }
        });
        // Advance to the next quote in lockstep with the photo
        quotePointerRef.current += 1;
        setQuoteIdx(quotePointerRef.current);
        scheduleNext();
      }, SLIDE_MS);
    }

    scheduleNext();

    return () => {
      cancelled = true;
      if (slideTimerRef.current) {
        clearTimeout(slideTimerRef.current);
        slideTimerRef.current = null;
      }
    };
  }, [active, photos.length]);

  // Live clock during slideshow
  useEffect(() => {
    if (!active) return;
    clockTimerRef.current = setInterval(() => setNow(new Date()), 30000);
    return () => {
      if (clockTimerRef.current) {
        clearInterval(clockTimerRef.current);
        clockTimerRef.current = null;
      }
    };
  }, [active]);

  // Reset slideshow state whenever it becomes active so it always starts fresh.
  useEffect(() => {
    if (active) {
      setLayerA(0);
      setLayerB(1);
      setFront("A");
      nextPhotoRef.current = 2;
      quotePointerRef.current = Math.floor(Math.random() * 100);
      setQuoteIdx(quotePointerRef.current);
    }
  }, [active]);

  if (!active) return null;

  const timeStr = now.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", hour12: true });
  const dateStr = now.toLocaleDateString([], {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  return (
    <div
      data-testid="slideshow-overlay"
      className="fixed inset-0 z-[200] cursor-none"
      style={{ background: "#000" }}
      onClick={() => setActive(false)}
      onTouchStart={() => setActive(false)}
    >
      {/* Layer A */}
      <img
        src={photos[layerA]}
        alt=""
        className="absolute inset-0 w-full h-full object-cover transition-opacity ease-in-out"
        draggable={false}
        style={{
          opacity: front === "A" ? 1 : 0,
          filter: "brightness(0.85)",
          transitionDuration: `${FADE_MS}ms`,
        }}
      />
      {/* Layer B */}
      <img
        src={photos[layerB]}
        alt=""
        className="absolute inset-0 w-full h-full object-cover transition-opacity ease-in-out"
        draggable={false}
        style={{
          opacity: front === "B" ? 1 : 0,
          filter: "brightness(0.85)",
          transitionDuration: `${FADE_MS}ms`,
        }}
      />

      {/* Gradient vignette for text legibility */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0) 35%, rgba(0,0,0,0) 65%, rgba(0,0,0,0.45) 100%)",
        }}
      />

      {/* Centered inspirational quote */}
      {currentQuote && (
        <div
          data-testid="slideshow-quote"
          key={`${quoteIdx}-${currentQuote.q}`}
          className="absolute inset-0 flex items-center justify-center pointer-events-none px-16 quote-fade-in"
        >
          <div
            className="max-w-4xl text-center select-none"
            style={{
              textShadow: "0 4px 28px rgba(0,0,0,0.65)",
            }}
          >
            <div
              className="font-serif text-[#EAE4DB] italic"
              style={{
                fontSize: "clamp(2rem, 4vw, 3.75rem)",
                lineHeight: 1.25,
                fontWeight: 300,
              }}
            >
              &ldquo;{currentQuote.q}&rdquo;
            </div>
            {currentQuote.a && (
              <div
                className="mt-6 font-mono uppercase tracking-[0.3em] text-[#EAE4DB]/75"
                style={{ fontSize: "clamp(0.7rem, 0.9vw, 0.95rem)" }}
              >
                — {currentQuote.a}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Clock + tap hint */}
      <div className="absolute top-10 left-12 text-[#EAE4DB] z-10 select-none">
        <div
          className="font-serif text-[8rem] leading-none tracking-tighter"
          style={{ fontWeight: 300, textShadow: "0 2px 24px rgba(0,0,0,0.4)" }}
        >
          {timeStr}
        </div>
        <div className="mt-2 text-lg font-mono uppercase tracking-[0.25em] text-[#EAE4DB]/85">
          {dateStr}
        </div>
      </div>

      <div className="absolute bottom-8 right-12 text-xs font-mono uppercase tracking-[0.25em] text-[#EAE4DB]/55 select-none">
        Tap anywhere to wake
      </div>

      {/* Top 3 to-dos */}
      {topTodos.length > 0 && (
        <div
          data-testid="slideshow-todos"
          className="absolute bottom-10 left-12 right-12 max-w-xl select-none"
        >
          <div className="text-[0.65rem] font-mono uppercase tracking-[0.25em] text-[#EAE4DB]/65 mb-3">
            On the list
          </div>
          <ul className="space-y-2">
            {topTodos.map((t) => {
              const assignees = (t.assignee_ids || [])
                .map((id) => familyById[id])
                .filter(Boolean);
              const primary = assignees[0];
              const todayIso = new Date().toISOString().slice(0, 10);
              const overdue = t.due_date && t.due_date < todayIso;
              const today = t.due_date === todayIso;
              let label = null;
              if (overdue) label = "OVERDUE";
              else if (today) label = "TODAY";
              else if (t.due_date) {
                const d = new Date(t.due_date + "T00:00:00");
                label = d.toLocaleDateString([], { weekday: "short" }).toUpperCase();
              }
              return (
                <li
                  key={t.id}
                  className="flex items-center gap-3"
                  style={{ textShadow: "0 2px 12px rgba(0,0,0,0.5)" }}
                >
                  <span
                    className="block w-2 h-2 rounded-full shrink-0"
                    style={{
                      backgroundColor: overdue ? "#E89090" : today ? "#F3C880" : primary?.color || "#EAE4DB",
                    }}
                  />
                  <span className="font-serif text-2xl text-[#EAE4DB] truncate">
                    {t.title}
                  </span>
                  {label && (
                    <span
                      className="text-[0.65rem] font-mono uppercase tracking-[0.18em] shrink-0"
                      style={{
                        color: overdue ? "#E89090" : today ? "#F3C880" : "#EAE4DB",
                        opacity: overdue || today ? 0.95 : 0.65,
                      }}
                    >
                      {label}
                    </span>
                  )}
                  {assignees.length > 0 && !overdue && !today && (
                    <span
                      className="text-[0.65rem] font-mono uppercase tracking-[0.18em] shrink-0"
                      style={{ color: primary.color, opacity: 0.8 }}
                    >
                      {assignees.map((a) => a.name).join(" · ")}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
